import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateSimple, PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PublicPaymentLinkModel,
	PublicPaymentLinkState,
	PublicPaymentMethodLinkSaveResultModel,
} from '@supply-mover-2-0/shared';
import { Toast, useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	PaymentLinkShell,
	PaymentLinkStatePanel,
	PaymentLinkSupportLine,
} from '../components/paymentLinks/PaymentLinkShell';
import {
	PortalAddPaymentMethod,
	type PortalPaymentMethodApiModel,
} from '../components/PortalAddPaymentMethod';
import { useTurnstile } from '../hooks/useTurnstile';
import {
	fetchPaymentLink,
	getPaymentLinkCaptureContext,
	PaymentLinkRequestError,
	savePaymentMethodThroughLink,
	toGatewayConfigResponse,
} from '../services/paymentLinkService';

/**
 * PD-16674 — `/payment-method/:token`: save a card or bank account to the
 * account, without signing in and without paying anything, from a link a
 * staff member sent. The method is vaulted exactly as one entered by staff;
 * the sender is notified each time.
 *
 * A link works for several saves until it expires, so after each one the
 * page confirms it and offers to add another.
 */
export function PaymentMethodLinkPage(): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const { token = '' } = useParams<{ token: string }>();

	const [link, setLink] = useState<PublicPaymentLinkModel | null>(null);
	const [state, setState] = useState<PublicPaymentLinkState | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saved, setSaved] =
		useState<PublicPaymentMethodLinkSaveResultModel | null>(null);
	// Remounts the card form for "Add another", so it starts empty with
	// fresh hosted fields.
	const [formKey, setFormKey] = useState(0);

	const load = useCallback(async (): Promise<void> => {
		setLoadError(null);
		try {
			const loaded = await fetchPaymentLink(token);
			setLink(loaded);
			setState(loaded.state);
		} catch (error) {
			if (error instanceof PaymentLinkRequestError && error.state != null) {
				setState(error.state);
				return;
			}
			setLoadError(
				error instanceof Error
					? error.message
					: 'This page could not be loaded.',
			);
		}
	}, [token]);

	useEffect(() => {
		void load();
	}, [load]);

	const turnstile = useTurnstile({
		siteKey:
			state === 'active' && saved == null
				? (link?.turnstileSiteKey ?? null)
				: null,
		action: link?.turnstileAction ?? 'payment-method-link',
		theme: isDark ? 'dark' : 'light',
	});

	// Read at submit time, so the form's calls stay one object (see PaymentLinkPage).
	const latestTurnstile = useRef(turnstile);
	latestTurnstile.current = turnstile;

	const api = useMemo<PortalPaymentMethodApiModel | null>(() => {
		if (link == null) {
			return null;
		}
		return {
			getGatewayConfig: async () => toGatewayConfigResponse(link),
			getCyberSourceCaptureContext: async (type) =>
				getPaymentLinkCaptureContext(token, type),
			submit: async (dto) => {
				const currentTurnstile = latestTurnstile.current;
				const turnstileToken = await currentTurnstile.getToken();
				if (turnstileToken == null) {
					throw new Error(t('Please complete the security check and try again.'));
				}
				try {
					setSaved(
						await savePaymentMethodThroughLink(token, {
							paymentMethod: dto,
							turnstileToken,
						}),
					);
					return true;
				} catch (error) {
					if (
						error instanceof PaymentLinkRequestError &&
						error.state != null &&
						error.state !== 'active'
					) {
						Toast.error(t(error.message));
						setState(error.state);
						return false;
					}
					throw new Error(
						t(
							error instanceof Error
								? error.message
								: 'The payment method could not be saved.',
						),
					);
				} finally {
					currentTurnstile.reset();
				}
			},
		};
	}, [link, token, t]);

	const initialPaymentType = useMemo(
		() =>
			link?.paymentTypes[0]?.paymentMethodType === 'ACH'
				? PaymentMethodTypeEnum.ACH
				: PaymentMethodTypeEnum.CREDIT_CARD,
		[link],
	);

	if (loadError != null) {
		return (
			<PaymentLinkShell>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 text-center space-y-4">
					<p className="text-sm text-danger">{t(loadError)}</p>
					<button
						type="button"
						onClick={() => {
							void load();
						}}
						className="rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Try again')}
					</button>
				</div>
			</PaymentLinkShell>
		);
	}

	if (state == null || link == null) {
		return (
			<PaymentLinkShell>
				<div className="flex items-center justify-center py-16">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
				</div>
			</PaymentLinkShell>
		);
	}

	if (state !== 'active') {
		return (
			<PaymentLinkShell>
				<PaymentLinkStatePanel state={state} />
			</PaymentLinkShell>
		);
	}

	if (saved != null) {
		return (
			<PaymentLinkShell>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3">
					<FontAwesomeIcon icon={faCircleCheck} className="text-4xl text-success" />
					<h1 className="text-2xl font-semibold text-default">
						{saved.alreadyOnFile
							? t('This payment method is already on your account')
							: t('Payment method saved')}
					</h1>
					<p className="text-sm text-brand-muted">
						{`${t(saved.paymentMethodLabel)} ${t('is on your account and can be used for future payments.')}`}
						{saved.isPrimary
							? ` ${t('It is now your default payment method.')}`
							: ''}
					</p>
					<p className="text-sm text-brand-muted">{t('Nothing was charged.')}</p>
					<div className="pt-2 flex flex-col items-center gap-3">
						<button
							type="button"
							onClick={() => {
								setSaved(null);
								setFormKey((key) => key + 1);
							}}
							className="rounded-md bg-portal-accent hover:bg-portal-accent-hover text-white font-medium px-4 py-2 transition-colors"
						>
							{t('Add another payment method')}
						</button>
						<PaymentLinkSupportLine />
					</div>
				</div>
			</PaymentLinkShell>
		);
	}

	return (
		<PaymentLinkShell>
			<div className="space-y-6">
				<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6 space-y-2">
					<h1 className="text-2xl font-semibold text-default">
						{t('Add a payment method')}
					</h1>
					<p className="text-sm text-brand-muted">{t(link.clientName ?? '')}</p>
					<p className="text-sm text-default">
						{t(
							'Save a card or bank account to your account for future payments. Nothing is charged now.',
						)}
					</p>
					{link.expiresAt != null ? (
						<p className="text-xs text-brand-muted">
							{`${t('This link works until')} ${t(formatDateSimple(link.expiresAt))}.`}
						</p>
					) : null}
				</section>

				<section className="bg-brand-surface border border-brand-border rounded-xl p-5 sm:p-6">
					{api != null ? (
						<PortalAddPaymentMethod
							key={formKey}
							api={api}
							onSuccess={() => undefined}
							supportedPaymentMethods={link.paymentTypes}
							preSelectedPaymentType={initialPaymentType}
							submitButtonLabel={t('Save Payment Method')}
							footerContent={
								<div className="space-y-2">
									{/* Turnstile draws here only when it needs the visitor to interact. */}
									<div ref={turnstile.containerRef} />
									{turnstile.failed ? (
										<p className="text-sm text-danger">
											{t(
												'The security check could not load. Please refresh the page and try again.',
											)}
										</p>
									) : null}
								</div>
							}
						/>
					) : null}
				</section>

				<div className="flex justify-center">
					<PaymentLinkSupportLine />
				</div>
			</div>
		</PaymentLinkShell>
	);
}
